/**
 * Error code constants and messages
 */

import { ErrorCode } from '../types';

export const ERROR_MESSAGES: Record<ErrorCode, string> = {
  // Authentication errors
  [ErrorCode.UNAUTHORIZED]: 'You must be logged in to access this resource',
  [ErrorCode.INVALID_TOKEN]: 'Invalid or malformed authentication token',
  [ErrorCode.TOKEN_EXPIRED]: 'Your session has expired. Please log in again',
  [ErrorCode.INSUFFICIENT_PERMISSIONS]: 'You do not have permission to perform this action',
  [ErrorCode.ACCOUNT_LOCKED]: 'Your account has been locked due to multiple failed login attempts',
  [ErrorCode.ACCOUNT_DELETED]:
    'This account has been deleted. Please sign up again to create a new account.',
  [ErrorCode.ACCOUNT_SUSPENDED]:
    'Your account has been suspended. Please contact support for assistance.',
  [ErrorCode.DELETION_CODE_EXPIRED]:
    'The deletion confirmation code has expired. Please request a new code.',
  [ErrorCode.DELETION_CODE_INVALID]:
    'The deletion confirmation code is invalid. Please check and try again.',

  // Validation errors
  [ErrorCode.VALIDATION_ERROR]: 'The provided data is invalid',
  [ErrorCode.INVALID_INPUT]: 'One or more fields contain invalid data',
  [ErrorCode.MISSING_REQUIRED_FIELD]: 'Required field is missing',
  [ErrorCode.INVALID_FORMAT]: 'The data format is incorrect',

  // Resource errors
  [ErrorCode.NOT_FOUND]: 'The requested resource was not found',
  [ErrorCode.ALREADY_EXISTS]: 'A resource with this identifier already exists',
  [ErrorCode.RESOURCE_CONFLICT]: 'The operation conflicts with the current state',

  // Business logic errors
  [ErrorCode.OPERATION_NOT_ALLOWED]: 'This operation is not allowed',
  [ErrorCode.QUOTA_EXCEEDED]: 'You have exceeded your quota limit',
  [ErrorCode.RATE_LIMITED]: 'Too many requests. Please try again later',
  [ErrorCode.SUBSCRIPTION_REQUIRED]: 'This feature requires a Pro subscription',

  // System errors
  [ErrorCode.INTERNAL_SERVER_ERROR]: 'An unexpected error occurred. Please try again later',
  [ErrorCode.DATABASE_ERROR]: 'A database error occurred',
  [ErrorCode.SERVICE_UNAVAILABLE]: 'The service is temporarily unavailable. Please try again later',
  [ErrorCode.EXTERNAL_SERVICE_ERROR]: 'An external service error occurred',

  // Tenant isolation errors
  [ErrorCode.TENANT_MISMATCH]: 'Tenant identifier mismatch detected',
  [ErrorCode.CROSS_TENANT_ACCESS]: 'Cross-tenant access is not allowed',

  // SKU/Subscription limit errors
  [ErrorCode.SKU_LIMIT_EXCEEDED]:
    'You have reached the limit for this resource on your current plan',
  [ErrorCode.FEATURE_NOT_AVAILABLE]: 'This feature is not available on your current plan',
  [ErrorCode.FILE_TYPE_NOT_ALLOWED]: 'This file type is not allowed on your current plan',
  [ErrorCode.FILE_SIZE_EXCEEDED]: 'File size exceeds the limit for your current plan',
  [ErrorCode.STORAGE_LIMIT_EXCEEDED]: 'You have exceeded your storage limit',
};

export const HTTP_STATUS_CODES: Record<ErrorCode, number> = {
  // Authentication errors
  [ErrorCode.UNAUTHORIZED]: 401,
  [ErrorCode.INVALID_TOKEN]: 401,
  [ErrorCode.TOKEN_EXPIRED]: 401,
  [ErrorCode.INSUFFICIENT_PERMISSIONS]: 403,
  [ErrorCode.ACCOUNT_LOCKED]: 403,
  [ErrorCode.ACCOUNT_DELETED]: 403,
  [ErrorCode.ACCOUNT_SUSPENDED]: 403,
  [ErrorCode.DELETION_CODE_EXPIRED]: 400,
  [ErrorCode.DELETION_CODE_INVALID]: 400,

  // Validation errors
  [ErrorCode.VALIDATION_ERROR]: 400,
  [ErrorCode.INVALID_INPUT]: 400,
  [ErrorCode.MISSING_REQUIRED_FIELD]: 400,
  [ErrorCode.INVALID_FORMAT]: 400,

  // Resource errors
  [ErrorCode.NOT_FOUND]: 404,
  [ErrorCode.ALREADY_EXISTS]: 409,
  [ErrorCode.RESOURCE_CONFLICT]: 409,

  // Business logic errors
  [ErrorCode.OPERATION_NOT_ALLOWED]: 403,
  [ErrorCode.QUOTA_EXCEEDED]: 429,
  [ErrorCode.RATE_LIMITED]: 429,
  [ErrorCode.SUBSCRIPTION_REQUIRED]: 402,

  // System errors
  [ErrorCode.INTERNAL_SERVER_ERROR]: 500,
  [ErrorCode.DATABASE_ERROR]: 500,
  [ErrorCode.SERVICE_UNAVAILABLE]: 503,
  [ErrorCode.EXTERNAL_SERVICE_ERROR]: 502,

  // Tenant isolation errors
  [ErrorCode.TENANT_MISMATCH]: 403,
  [ErrorCode.CROSS_TENANT_ACCESS]: 403,

  // SKU/Subscription limit errors
  [ErrorCode.SKU_LIMIT_EXCEEDED]: 403,
  [ErrorCode.FEATURE_NOT_AVAILABLE]: 403,
  [ErrorCode.FILE_TYPE_NOT_ALLOWED]: 403,
  [ErrorCode.FILE_SIZE_EXCEEDED]: 413,
  [ErrorCode.STORAGE_LIMIT_EXCEEDED]: 403,
};
